import type { MouseEvent, ReactElement } from 'react';
import { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { detectIfTruncated } from '../common-functions';
import OdinIcon from '../odin-icon/odin-icon.component';
import Tooltip from '../tooltip-component/tooltip.component';
import type { TabViewPanelProps } from './tab-view-panel.component';

export interface TabViewHeaderProps {
  tab: ReactElement<TabViewPanelProps>;
  activeIndex: number;
  currentIndex: number;
  onClick: (event: MouseEvent, tabIndex: number) => void;
  styles: {
    tabStyles?: string;
    activeTabStyles?: string;
    inactiveTabStyles?: string;
  };
}

function TabViewHeader({
  tab,
  activeIndex,
  currentIndex,
  onClick,
  styles: { tabStyles, activeTabStyles, inactiveTabStyles },
}: TabViewHeaderProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    ref.current && setIsTruncated(detectIfTruncated(ref.current));
  }, []);

  useEffect(() => {
    if (activeIndex === currentIndex) {
      ref.current?.scrollIntoView(true);
    }
  }, [activeIndex, currentIndex]);

  const tabFinalStyles = clsx(tabStyles, {
    [`${activeTabStyles}`]: activeIndex === currentIndex,
    [`${inactiveTabStyles}`]: activeIndex !== currentIndex,
  });

  return (
    <li className={tabFinalStyles} role="presentation" onClick={(event) => onClick(event, currentIndex)}>
      <a className="odin-flex" role="tab">
        {tab.props.leftIcon && <OdinIcon icon={tab.props.leftIcon} />}
        <Tooltip
          description={tab.props.header}
          tooltipClassName="odin-bg-mono-1"
          disabled={!isTruncated}
          tooltipZIndexCheck={true}
        >
          <div ref={ref} className="odin-max-w-48 odin-grow odin-truncate">
            {tab.props.header}
          </div>
        </Tooltip>
        {tab.props.rightIcon && <OdinIcon icon={tab.props.rightIcon} />}
      </a>
    </li>
  );
}

export default TabViewHeader;
